import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { Link } from 'react-router-dom';
import { Target, Heart, Users, Award, Sparkles, ArrowRight, CheckCircle2 } from 'lucide-react';
import { siteImages } from '../data/mock';

const OurMissionPage = () => {
  useEffect(() => {
    document.title = 'Our Mission | Rad Tech Foundry';
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />
      
      <main className="pt-[104px]">
        {/* Banner Section */}
        <section className="keep-dark relative h-64 md:h-80 overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src={siteImages.heroLifestyle}
              alt="Our Mission"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-dark-950 via-dark-950/80 to-dark-950/60" />
          </div>
          <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-athletic-500/10 border border-athletic-500/30 rounded-full text-athletic-400 text-xs font-semibold mb-4">
                <Target className="w-3.5 h-3.5" />
                OUR PURPOSE
              </div>
              <h1 className="font-display text-4xl md:text-5xl text-white font-extrabold mb-3">
                Our Mission
              </h1>
              <p className="text-dark-300 max-w-lg">
                Empowering tech lovers to achieve their best performance
              </p>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Mission Statement */}
          <div className="bg-dark-900 border border-dark-800 rounded-2xl p-8 md:p-12 mb-16 text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-athletic-500 to-athletic-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Sparkles className="w-10 h-10 text-white" />
            </div>
            <h2 className="font-display text-3xl lg:text-4xl text-white font-extrabold mb-6">
              Power Up. Stay Ahead.
            </h2>
            <p className="text-dark-300 text-lg leading-relaxed max-w-3xl mx-auto">
              At Rad Tech Foundry, our mission is simple: to provide tech lovers with the premium tech gear they need to achieve their goals. We believe that the right gear can transform your setup, and we're dedicated to curating only the best products for our customers.
            </p>
          </div>

          {/* Core Values */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 bg-athletic-500/10 text-athletic-400 text-xs font-bold uppercase tracking-wider rounded-full mb-4 border border-athletic-500/20">
                Core Values
              </span>
              <h2 className="font-display text-3xl lg:text-4xl text-white font-extrabold">
                What We Stand For
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Target, title: 'Excellence', desc: 'We strive for excellence in every product we offer and every interaction with our customers.' },
                { icon: Heart, title: 'Passion', desc: 'We\'re passionate about technology and helping tech lovers get the most out of every device.' },
                { icon: Users, title: 'Community', desc: 'We believe in building a community of tech lovers who support and inspire each other.' },
                { icon: Award, title: 'Quality', desc: 'We never compromise on quality. Every product meets our high standards.' },
              ].map((value, i) => (
                <div key={i} className="bg-dark-900 border border-dark-800 rounded-2xl p-6 hover:border-athletic-500/50 transition-all group">
                  <div className="w-14 h-14 bg-athletic-500/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-athletic-500/20 transition-colors">
                    <value.icon className="w-7 h-7 text-athletic-400" />
                  </div>
                  <h3 className="font-display font-bold text-white text-lg mb-2">{value.title}</h3>
                  <p className="text-dark-400 text-sm leading-relaxed">{value.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Our Promise */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div className="relative">
              <div className="aspect-video rounded-2xl overflow-hidden border border-dark-800">
                <img 
                  src={siteImages.powerBackground}
                  alt="Our Promise"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div>
              <span className="inline-block px-4 py-1.5 bg-athletic-500/10 text-athletic-400 text-xs font-bold uppercase tracking-wider rounded-full mb-4 border border-athletic-500/20">
                Our Promise
              </span>
              <h2 className="font-display text-3xl text-white font-extrabold mb-6">
                We're Here for Your Journey
              </h2>
              <p className="text-dark-300 leading-relaxed mb-6">
                Whether you're just starting your tech journey or you're a seasoned tech pro, we're here to support you. Our team is dedicated to helping you find the right gear for your goals.
              </p>
              <div className="space-y-4">
                {[
                  'Premium quality premium tech',
                  'Fast shipping across the USA',
                  '30-day hassle-free returns',
                  'Dedicated customer support',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-athletic-400 flex-shrink-0" />
                    <span className="text-white">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center bg-gradient-to-br from-athletic-500/10 to-athletic-600/10 border border-athletic-500/30 rounded-2xl p-12">
            <h2 className="font-display text-3xl font-extrabold text-white mb-4">
              Join the Rad Tech Foundry Community
            </h2>
            <p className="text-dark-300 mb-8 max-w-xl mx-auto">
              Start your journey with premium tech gear designed for performance.
            </p>
            <Link 
              to="/shop"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-athletic-500 to-athletic-600 text-white font-bold uppercase tracking-wider rounded-xl hover:from-athletic-400 hover:to-athletic-500 transition-all shadow-lg"
            >
              Shop Now
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default OurMissionPage;
